/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {ErrorType, getMode, getSearchParams, ResponseData, showSnackbar} from '@dvrd/dvr-controls';

import React, {useContext, useEffect, useRef, useState} from 'react';

import {AppContext} from '../../misc/appContext';

import {authorizeBolOAuth} from '../../../util/requestSender';

import ApiKey from '../../../models/apiKeyModel';

import BolProduct from '../../../models/bolProductModel';

import Account from '../../../models/accountModel';

import {APIValidationStatus} from '../../../util/interfaces';

import {onConnectBolOauth} from '../../../util/bolOauthUtil';

import BolOauth from './bolOauth';

import {getFromLocal} from '../../../util/storageUtil';

import {
    CONNECTION_COMPLETE_ROUTE,
    OAUTH_SALES_DASHBOARD_REDIRECT,
    OAUTH_SEARCH_RANKINGS_REDIRECT,
    SALES_DASHBOARD_V2_ROUTE,
    SEARCH_RANKINGS_ROUTE,
    SELECTED_ACCOUNT_KEY
} from '../../../util/constants';

import {useNavigate} from 'react-router';

export default function BolOauthController() {
    const {onChangeContext, accountContext} = useContext(AppContext);
    const {account} = accountContext;
    const navigate = useNavigate();
    const [status, setStatus] = useState<APIValidationStatus>(APIValidationStatus.VALIDATING);
    const [error, setError] = useState<ErrorType>(null);
    const oauthState = useRef<string | null>(null);

    function onClose() {
        navigate('/facturen');
    }

    function onRetry(evt: React.MouseEvent) {
        onConnectBolOauth(evt.shiftKey);
    }

    function mount() {
        const params = getSearchParams(window.location.search, true);
        oauthState.current = params.get('state');
        if (params.has('error-description')) {
            setError(params.get('error-description'));
            setStatus(APIValidationStatus.ERROR);
        } else if (params.has('code') || getMode() !== 'prod') {
            const code: string = params.get('code') ?? '';
            setStatus(APIValidationStatus.VALIDATING);
            setError(null);
            authorizeCode(code);
        } else {
            setStatus(APIValidationStatus.ERROR);
            setError('We hebben geen autorisatiecode ontvangen.');
        }
    }

    function authorizeCode(code: string) {
        let accountId = getFromLocal(SELECTED_ACCOUNT_KEY);
        if (!accountId) {
            if (!account) return;
            accountId = account.id;
        }
        authorizeBolOAuth({
            data: {code, account_id: accountId},
            callback: (data: ResponseData) => {
                if (data.success) {
                    const apiKey = new ApiKey(data.api_key);
                    if (account) {
                        const _account = account.clone();
                        _account.replaceApiKey(apiKey);
                        onChangeContext({account: _account});
                    }
                    if (data.is_new) {
                        navigate(CONNECTION_COMPLETE_ROUTE);
                        importProducts(apiKey);
                    } else if (oauthState.current) {
                        switch (oauthState.current) {
                            case OAUTH_SEARCH_RANKINGS_REDIRECT:
                                showSnackbar('De koppeling met Bol is vernieuwd');
                                navigate(SEARCH_RANKINGS_ROUTE);
                                break;
                            case OAUTH_SALES_DASHBOARD_REDIRECT:
                                showSnackbar('De koppeling met Bol is vernieuwd');
                                navigate(SALES_DASHBOARD_V2_ROUTE);
                                break;
                            default:
                                setStatus(APIValidationStatus.VALIDATED);
                                break;
                        }
                    } else setStatus(APIValidationStatus.VALIDATED);
                } else {
                    setStatus(APIValidationStatus.ERROR);
                    setError(data.message ?? 'Het verbinden met de API van Bol is niet gelukt.');
                }
            }
        });
    }

    function importProducts(apiKey: ApiKey) {
        BolProduct.importAsync(apiKey.accountId, false, (account: Account | null) => {
            // We don't check success status here, it doesn't matter much if the product import failed here
            setStatus(APIValidationStatus.VALIDATED);
            onChangeContext({account});
        });
    }

    useEffect(() => {
        mount();
    }, []);

    return <BolOauth onClose={onClose} onRetry={onRetry} status={status} error={error} />;
}
